/**
 * Belt configurator.
 *
 * Size and colour resolve to a real variant, which supplies the price and the
 * inventory behind the scarcity line. Plate choice is a line item property, and
 * the knife is a second product added in the same cart request.
 *
 * The section renders every panel and a working <form action="/cart/add">, so
 * with JavaScript unavailable the product is still configurable and buyable.
 * Everything below is enhancement on top of that.
 */
(function () {
  'use strict';

  const STEPS = ['intro', 'size', 'color', 'plate', 'knife', 'summary'];

  /** Escapes a value for safe insertion into HTML. */
  function escapeHtml(value) {
    return String(value).replace(
      /[&<>"']/g,
      (char) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]
    );
  }

  class KalloConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialised) return;
      this.dataset.initialised = 'true';

      const dataEl = this.querySelector('[data-configurator-variants]');
      if (!dataEl) return;

      try {
        this.variants = JSON.parse(dataEl.textContent);
      } catch (error) {
        console.warn('Configurator: could not parse variant data.', error);
        return;
      }

      // Belt cut-outs keyed by colour. Empty is fine — the base image is then
      // used for every colour, which is the right fallback while only one
      // colour has been photographed.
      const basesEl = this.querySelector('[data-configurator-color-bases]');
      try {
        this.colorBases = basesEl ? JSON.parse(basesEl.textContent) : {};
      } catch {
        this.colorBases = {};
      }

      // The plate as a product, so the chosen face can go in the cart as its own
      // line rather than only as a note on the belt. Absent is fine — without a
      // plate product the note is all there is, which is the old behaviour.
      const platesEl = this.querySelector('[data-configurator-plates]');
      try {
        this.plateProduct = platesEl ? JSON.parse(platesEl.textContent) : null;
      } catch {
        this.plateProduct = null;
      }

      this.sizeIndex = Number(this.dataset.sizeIndex);
      this.colorIndex = Number(this.dataset.colorIndex);
      this.lowStockThreshold = Number(this.dataset.lowStockThreshold || 0);
      this.demoStock = this.hasAttribute('data-demo-stock-min')
        ? {
            min: Number(this.dataset.demoStockMin),
            max: Math.max(Number(this.dataset.demoStockMin), Number(this.dataset.demoStockMax)),
          }
        : null;
      this.knifeVariantId = this.dataset.knifeVariant || null;
      this.knifePrice = Number(this.dataset.knifePrice || 0);
      this.knifeCompare = Number(this.dataset.knifeCompare || 0);

      // The word shown instead of a zero price — "FREE" — for the free-belt
      // offer. Empty keeps zero amounts formatted as money.
      this.freeLabel = this.dataset.freeLabel || '';

      // Preview flag: treat the belt as free (its price counts as zero, its real
      // price struck through) without the variant actually being zero. For
      // previewing the free-offer layout only — the cart still charges the real
      // price, so it is turned off before the store opens.
      this.demoFree = this.hasAttribute('data-demo-free');

      // Free offer: the selection bar prices add-ons as they are committed to
      // (FREE until the plate step is behind them), rather than all at once.
      this.barStaged = this.hasAttribute('data-bar-staged');

      // Without both options resolved there is no variant to select, so leave
      // the server-rendered form exactly as it is.
      if (this.sizeIndex < 0 || this.colorIndex < 0) return;

      this.form = this.querySelector('[data-configurator-form]');
      this.variantInput = this.querySelector('[data-variant-input]');
      this.beltImage = this.querySelector('[data-belt-image]');
      this.plateOverlay = this.querySelector('[data-plate-overlay]');
      this.panels = new Map(
        Array.from(this.querySelectorAll('[data-step]')).map((el) => [el.dataset.step, el])
      );

      // The base image as rendered, kept so a colour with no cut-out of its own
      // falls back to it rather than to a catalogue shot on a grey panel.
      this.defaultBase = this.beltImage?.getAttribute('src') || null;

      this.currentStep = 'intro';
      this.gateSections();
      this.bindEvents();
      this.updateUpsell();
      this.showStep(this.startingStep(), { focus: false });
    }

    /**
     * Which step to open on.
     *
     * A link can name one as `#step-<name>` — the product page's Configure
     * button opens `#step-size`, because a visitor arriving from there has
     * just read the introduction and does not need it again. The prefix keeps
     * the fragment from colliding with an element id elsewhere on the page.
     *
     * Anything unrecognised falls back to the opening step, so a stale or
     * hand-edited link degrades to the full sequence rather than to nothing.
     */
    startingStep() {
      const named = (window.location.hash || '').replace(/^#step-/, '');
      return STEPS.includes(named) ? named : 'intro';
    }

    /**
     * Everything below the configurator describes the belt the visitor has not
     * finished building yet, so it is held back until the summary. Done from
     * script rather than Liquid: without JavaScript there are no steps to work
     * through, and the whole page should just be there.
     */
    gateSections() {
      if (!this.hasAttribute('data-gate-sections')) return;

      const section = this.closest('.shopify-section');
      const gated = [];
      let sibling = section?.nextElementSibling;
      while (sibling) {
        if (sibling.classList.contains('shopify-section')) gated.push(sibling);
        sibling = sibling.nextElementSibling;
      }
      this.gatedSections = gated;
      this.setSectionsVisible(false);
    }

    setSectionsVisible(visible) {
      this.gatedSections?.forEach((section) => {
        section.toggleAttribute('data-cfg-gated', !visible);
      });
    }

    bindEvents() {
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-option-input], [data-plate-input], [data-knife-input]')) {
          this.update();
        }
        if (event.target.matches('[data-upsell-variant]')) this.upsellChosen = true;
        if (event.target.matches('[data-upsell-toggle], [data-upsell-variant]')) {
          this.updateUpsell();
          this.update();
        }
      });

      this.addEventListener('click', (event) => {
        const next = event.target.closest('[data-step-next]');
        const back = event.target.closest('[data-step-back]');
        const goto = event.target.closest('[data-goto-step]');
        const knife = event.target.closest('[data-knife-choice]');
        const platePrev = event.target.closest('[data-plate-prev]');
        const plateNext = event.target.closest('[data-plate-next]');

        // Yes and no are both answers, so either one moves the visitor on.
        if (knife) {
          const wanted = knife.dataset.knifeChoice;
          const input = this.querySelector(`[data-knife-input][value="${wanted}"]`);
          if (input) input.checked = true;
          this.update();
          this.showStep('summary');
          return;
        }

        if (platePrev) return this.slidePlate(-1);
        if (plateNext) return this.slidePlate(1);

        if (next) this.move(1);
        else if (back) this.move(-1);
        else if (goto) this.showStep(goto.dataset.gotoStep);
      });

      this.form?.addEventListener('submit', (event) => this.onSubmit(event));
    }

    /* ---- Plate slider ---- */

    /**
     * Moves the plate strip by one, selecting as it goes: the plate on screen
     * is the plate chosen, so there is no separate confirm step. Wraps, so the
     * strip reads as a loop like the reference's dials.
     */
    slidePlate(direction) {
      const inputs = Array.from(this.querySelectorAll('[data-plate-input]'));
      if (!inputs.length) return;

      const current = inputs.findIndex((input) => input.checked);
      const from = current < 0 ? 0 : current;
      const target = (from + direction + inputs.length) % inputs.length;

      inputs[target].checked = true;
      this.update();

      // The wide layout slides the whole row under a fixed centre, so the
      // chosen plate arrives there on its own. The narrow one is a strip that
      // scrolls, so it has to be brought into view — centred where there is
      // room to centre it, and left alone when the strip does not overflow.
      const track = this.querySelector('[data-plate-track]');
      const plate = inputs[target].closest('.cfg__plate');
      // Only when the track is genuinely a scroll container. The wide layout
      // clips it with `overflow: hidden` and positions the plates by transform,
      // and `scrollLeft` is still writable on a clipped box — setting it there
      // would shove the carousel sideways behind its own mask.
      const scrolls = track && getComputedStyle(track).overflowX !== 'hidden';
      if (scrolls && plate && track.scrollWidth > track.clientWidth) {
        const centred = plate.offsetLeft - (track.clientWidth - plate.offsetWidth) / 2;
        track.scrollLeft = Math.max(0, centred);
      }
    }

    /* ---- Step navigation ---- */

    move(direction) {
      const index = STEPS.indexOf(this.currentStep);
      const target = STEPS[index + direction];
      if (target) this.showStep(target);
    }

    showStep(step, { focus = true } = {}) {
      if (!this.panels.has(step)) return;
      this.currentStep = step;
      // Published on the host so the stylesheet can lay a step out differently —
      // step 1 introduces the belt rather than configuring it.
      this.dataset.step = step;

      this.panels.forEach((panel, name) => {
        panel.hidden = name !== step;
      });

      this.querySelectorAll('[data-goto-step]').forEach((dot) => {
        const isCurrent = dot.dataset.gotoStep === step;
        dot.setAttribute('aria-current', isCurrent ? 'step' : 'false');
      });

      this.updateChrome(step);

      if (focus) {
        // Move focus into the newly shown panel so keyboard and screen reader
        // users land on the step they just opened rather than staying on a
        // button that is now hidden.
        const panel = this.panels.get(step);
        const target = panel.querySelector('input, button, [tabindex]');
        target?.focus({ preventScroll: true });
        panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }

      this.update();
    }

    /**
     * Everything around the panels that changes per step: which navigation
     * button is live, what the forward button is called, and which of the
     * step rail, plate chooser and 360 control belong on screen.
     */
    updateChrome(step) {
      const index = STEPS.indexOf(step);
      const nextStep = STEPS[index + 1];
      const isLast = !nextStep;

      const back = this.querySelector('[data-step-back]');
      const label = this.querySelector('[data-next-label]');
      const navSubmit = this.querySelector('[data-nav-submit]');

      if (back) back.hidden = index === 0;
      this.querySelectorAll('[data-step-next]').forEach((next) => {
        next.hidden = isLast;
      });

      // On the summary the add-to-cart button inside the panel is the one that
      // shows, beside the price — so the one in the navigation bar stands down.
      // It has to be addressed by name: `[data-add-to-cart]` matches the panel's
      // button first, so selecting on that hid the wrong one and the summary was
      // left with no way to buy. The navigation copy exists for the no-JS path,
      // where CSS reveals it, so with script running it stays down throughout.
      if (navSubmit) navSubmit.hidden = true;

      // The forward button is named after the step it leads to, so the visitor
      // knows what is coming rather than reading a generic "Continue". Leaving
      // the finished belt is the exception: there the button is the invitation
      // to start, not the name of what follows.
      if (label && nextStep) {
        label.textContent =
          step === 'intro'
            ? this.t('start', 'Configure')
            : this.panels.get(nextStep)?.dataset.stepLabel || '';
      }

      // The rail is out on the two steps that are not part of choosing: the
      // opening one, where nothing has been chosen yet to show, and the
      // summary, where the configuring is finished and a row of steps would
      // point back into it rather than on to the cart. The navigation bar's
      // back button is the way back from there. Every choosing step shows it,
      // the plate step included, so the visitor always sees where they are.
      const RAIL_HIDDEN_ON = ['intro', 'summary'];
      const plateRow = this.querySelector('[data-plate-row]');
      const rail = this.querySelector('[data-configurator-progress]');
      if (plateRow) plateRow.hidden = step !== 'plate';

      // The knife step shows the knife gallery in the belt's place. A hidden
      // box loses its scroll position, so the gallery is put back on the
      // picture it was showing on the way in, and a video is stopped on the
      // way out rather than left playing unseen.
      const knifeMedia = this.querySelector('[data-knife-media]');
      if (knifeMedia) {
        knifeMedia.hidden = step !== 'knife';
        if (step === 'knife') knifeMedia.go?.(knifeMedia.index || 0);
        else knifeMedia.querySelectorAll('video').forEach((video) => video.pause());
      }
      if (rail) rail.hidden = RAIL_HIDDEN_ON.includes(step);

      const view360 = this.querySelector('kallo-360');
      if (view360) view360.hidden = !isLast;

      // The rest of the page belongs to the finished belt.
      this.setSectionsVisible(isLast);
    }

    /* ---- State ---- */

    get selectedSize() {
      return this.querySelector('[data-option-input="size"]:checked')?.value || null;
    }

    get selectedColor() {
      return this.querySelector('[data-option-input="color"]:checked')?.value || null;
    }

    get selectedPlate() {
      return this.querySelector('[data-plate-input]:checked') || null;
    }

    get knifeSelected() {
      return this.querySelector('[data-knife-input]:checked')?.value === 'yes';
    }

    /**
     * The plate variant matching the belt's colour and the chosen face.
     *
     * The plate is sold in the same colours as the belt, so a brown belt takes
     * a brown plate without the visitor being asked twice. A plate product with
     * no colour option is matched on the design alone.
     */
    findPlateVariant() {
      const data = this.plateProduct;
      const design = this.selectedPlate?.dataset.designValue;
      if (!data || !data.variants.length || !design) return null;

      if (data.designIndex < 0) return null;

      const color = this.selectedColor;
      return (
        data.variants.find((variant) => {
          if (variant.options[data.designIndex] !== design) return false;
          if (data.colorIndex < 0) return true;
          return variant.options[data.colorIndex] === color;
        }) || null
      );
    }

    /**
     * The upsell variant, when its box is ticked: the option chosen in its
     * select, or the single hidden input when there is only one to offer.
     */
    get selectedUpsell() {
      if (!this.querySelector('[data-upsell-toggle]')?.checked) return null;
      const field = this.querySelector('[data-upsell-variant]');
      if (!field) return null;
      const source = field.tagName === 'SELECT' ? field.selectedOptions[0] : field;
      if (!source) return null;
      return {
        id: Number(field.value),
        price: Number(source.dataset.price || 0),
        compare: Number(source.dataset.compare || 0),
        image: source.dataset.image || '',
      };
    }

    /**
     * The upsell card's price, saving and picture follow its select. The
     * saving is worked out from the compare-at price, so it only ever states
     * the discount the store has set.
     */
    updateUpsell() {
      const field = this.querySelector('[data-upsell-variant]');
      if (!field) return;

      // "More plates" means a different one: until the visitor picks for
      // themselves, the offer starts on the first plate that is not the one
      // already on the belt.
      if (field.tagName === 'SELECT' && !this.upsellChosen) {
        const configured = String(this.findPlateVariant()?.id || '');
        const other = [...field.options].find((option) => option.value !== configured);
        if (other) field.value = other.value;
      }

      const source = field.tagName === 'SELECT' ? field.selectedOptions[0] : field;
      if (!source) return;

      const price = Number(source.dataset.price || 0);
      const compare = Number(source.dataset.compare || 0);
      const onSale = compare > price;

      const priceEl = this.querySelector('[data-upsell-price]');
      if (priceEl) priceEl.textContent = this.formatMoney(price);

      const compareEl = this.querySelector('[data-upsell-compare]');
      if (compareEl) {
        compareEl.textContent = onSale ? this.formatMoney(compare) : '';
        compareEl.hidden = !onSale;
      }

      const badge = this.querySelector('[data-upsell-badge]');
      if (badge) {
        const percent = onSale ? Math.round((1 - price / compare) * 100) : 0;
        badge.textContent = percent > 0
          ? this.t('percent_off', '[percent]% off').replace('[percent]', percent)
          : '';
        badge.hidden = percent <= 0;
      }

      const image = this.querySelector('.cfg__upsell-media img');
      if (image && source.dataset.image) {
        image.src = source.dataset.image;
        image.removeAttribute('srcset');
      }
    }

    findVariant(size, color) {
      return this.variants.find(
        (variant) =>
          variant.options[this.sizeIndex] === size &&
          variant.options[this.colorIndex] === color
      );
    }

    /* ---- Rendering ---- */

    update() {
      const variant = this.findVariant(this.selectedSize, this.selectedColor);
      this.currentVariant = variant;

      this.updateVariantInput(variant);
      this.updateChoiceAvailability();
      this.updateMedia(variant);
      this.updateStock(variant);
      this.updateProgressThumbs();
      this.updateDetail();
      this.updateUpsell();
      this.updateSummary(variant);
      this.updateBar(variant);
      this.updateStatement();
    }

    /**
     * Fills the statement panel's tokens with the current configuration.
     *
     * The panel is its own section, so it is queried from the document rather
     * than from this element. Values are escaped before being inserted, since
     * the sentence is assembled as HTML in order to highlight each choice.
     */
    updateStatement() {
      const targets = document.querySelectorAll('[data-config-statement]');
      if (!targets.length) return;

      const values = {
        size: this.selectedSize,
        color: this.selectedColor,
        plate: this.selectedPlate?.value,
        knife: this.knifeSelected ? this.t('included', 'Included') : '',
      };

      targets.forEach((el) => {
        const template = el.dataset.template || '';
        el.innerHTML = template.replace(/\[(size|color|plate|knife)\]/g, (_, key) => {
          const value = values[key];
          return value ? `<mark>${escapeHtml(value)}</mark>` : '';
        });
      });
    }

    /** The pinned bar's running price, and the plate caption. */
    updateBar(variant) {
      // The plate step has no visible chooser label of its own, so the caption
      // under its heading names the current pick.
      const plateName = this.querySelector('[data-plate-name]');
      if (plateName) plateName.textContent = this.selectedPlate?.value || '';

      // The plate's upcharge on the plate step — "+$19" — when it is charged on
      // top of the belt rather than included in its price.
      const platePrice = this.querySelector('[data-plate-price]');
      if (platePrice) {
        const plateVariant = this.findPlateVariant();
        const charged =
          this.plateProduct?.included === false &&
          plateVariant &&
          plateVariant.available &&
          plateVariant.price > 0;
        platePrice.textContent = charged ? `+${this.formatMoney(plateVariant.price)}` : '';
        platePrice.hidden = !charged;
      }

      const total = this.querySelector('[data-total]');
      if (total && variant) {
        total.textContent = this.formatMoney(this.runningTotal(variant));
      }
      this.updateOffer(variant);

      // Free offer: the bar prices each add-on as the visitor commits to it,
      // rather than showing the whole total at once. It stays at the belt's
      // price (FREE) through size, colour and plate; the plate joins once its
      // step is behind them, the knife once added — so the bar climbs toward the
      // summary total. Runs after updateOffer, overriding the bar it just set
      // while leaving the summary's own full total alone.
      if (this.barStaged && variant) this.updateBarStaged(variant);
    }

    /**
     * Rewrites the selection bar's price trio to the running total of what the
     * visitor has committed to so far: the belt always (FREE), the plate once
     * its step is behind them, the knife once added. Scoped to the bar, so the
     * summary's full itemised total is untouched.
     */
    updateBarStaged(variant) {
      const bar = this.querySelector('.cfg__detail-price');
      if (!bar) return;

      let now = this.beltNow(variant);
      let was = this.beltWas(variant);

      // The plate counts from the step after its own — on the plate step the
      // visitor is still choosing it, so it has not been committed to yet.
      const order = ['intro', 'size', 'color', 'plate', 'knife', 'summary'];
      const platePassed = order.indexOf(this.currentStep) > order.indexOf('plate');
      const plateVariant = this.findPlateVariant();
      if (
        platePassed &&
        plateVariant &&
        plateVariant.available &&
        this.plateProduct?.included === false
      ) {
        now += plateVariant.price;
        was += Math.max(plateVariant.price, plateVariant.compareAtPrice || 0);
      }

      if (this.knifeSelected) {
        now += this.knifePrice;
        was += Math.max(this.knifePrice, this.knifeCompare || 0);
      }

      const saving = was - now;
      const template = this.dataset.saveText || '';

      const totalEl = bar.querySelector('[data-total]');
      if (totalEl) totalEl.textContent = this.formatMoney(now);

      const compareEl = bar.querySelector('[data-total-compare]');
      if (compareEl) {
        compareEl.textContent = saving > 0 ? this.formatMoney(was) : '';
        compareEl.hidden = saving <= 0;
      }

      const saveEl = bar.querySelector('[data-total-save]');
      if (saveEl) {
        // While the bar reads FREE the struck "was" already tells the story, and
        // a "100% off" badge only repeats it — so the badge shows only once
        // there is a price left to discount.
        const show = saving > 0 && now > 0 && template !== '';
        saveEl.textContent = show
          ? template
              .replace('[amount]', this.formatMoney(saving))
              .replace('[percent]', String(Math.round((saving / was) * 100)))
          : '';
        saveEl.hidden = !show;
      }
    }

    /**
     * What the same choices would cost at their compare-at prices. A part
     * with no compare-at price, or one below its price, counts at its price.
     */
    runningCompare(variant) {
      const was = (price, compare) => Math.max(price, compare || 0);
      let cents = was(variant.price, variant.compareAtPrice);
      if (this.knifeSelected) cents += was(this.knifePrice, this.knifeCompare);

      const upsell = this.selectedUpsell;
      if (upsell) cents += was(upsell.price, upsell.compare);

      const plateVariant = this.findPlateVariant();
      if (plateVariant && plateVariant.available && this.plateProduct?.included === false) {
        cents += was(plateVariant.price, plateVariant.compareAtPrice);
      }
      return cents;
    }

    /** The struck-through total and the saving badge, beside each total. */
    updateOffer(variant) {
      const total = variant ? this.runningTotal(variant) : 0;
      const compare = variant ? this.runningCompare(variant) : 0;
      const saving = compare - total;
      const template = this.dataset.saveText || '';

      this.querySelectorAll('[data-total-compare]').forEach((el) => {
        el.textContent = saving > 0 ? this.formatMoney(compare) : '';
        el.hidden = saving <= 0;
      });
      this.querySelectorAll('[data-total-save]').forEach((el) => {
        el.textContent =
          saving > 0
            ? template
                .replace('[amount]', this.formatMoney(saving))
                .replace('[percent]', String(Math.round((saving / compare) * 100)))
            : '';
        el.hidden = saving <= 0 || !template;
      });
    }

    /**
     * Belt, plus the knife if it was added, plus the plate unless it comes with
     * the belt. When the plate is marked as included the total leaves it out,
     * on the basis that an automatic discount takes it to zero at the cart —
     * showing it here and then not charging it would be the more confusing of
     * the two mistakes.
     */
    /** The belt's own contribution to the total — zero when previewing free. */
    beltNow(variant) {
      return this.demoFree ? 0 : variant.price;
    }

    /** What the belt alone would have cost — its compare-at price if higher. */
    beltWas(variant) {
      return Math.max(variant.price, variant.compareAtPrice || 0);
    }

    runningTotal(variant) {
      let cents = this.beltNow(variant);
      if (this.knifeSelected) cents += this.knifePrice;

      const upsell = this.selectedUpsell;
      if (upsell) cents += upsell.price;

      const plateVariant = this.findPlateVariant();
      if (plateVariant && plateVariant.available && this.plateProduct?.included === false) {
        cents += plateVariant.price;
      }
      return cents;
    }

    updateVariantInput(variant) {
      if (variant) this.variantInput.value = variant.id;

      const buyable = Boolean(variant && variant.available);
      this.querySelectorAll('[data-add-to-cart]').forEach((submit) => {
        submit.disabled = !buyable;
        submit.setAttribute('aria-disabled', String(!buyable));
        submit.textContent = buyable
          ? this.t('add_to_cart', 'Add to cart')
          : this.t('sold_out', 'Sold out');
      });
    }

    /**
     * Marks combinations that are sold out without taking them away. This is a
     * configurator before it is a checkout: the visitor is here to see the belt
     * they would build, so every size and colour stays selectable and the
     * product image still swaps. Only the purchase is gated, by the add-to-cart
     * button and the scarcity badge.
     */
    updateChoiceAvailability() {
      const soldOut = this.t('sold_out', 'Sold out');
      const mark = (input, available) => {
        input.closest('.cfg__choice')?.classList.toggle('is-unavailable', !available);
        if (available) {
          input.removeAttribute('aria-label');
        } else {
          input.setAttribute('aria-label', `${input.value} — ${soldOut}`);
        }
      };

      const size = this.selectedSize;
      const color = this.selectedColor;

      this.querySelectorAll('[data-option-input="color"]').forEach((input) => {
        const variant = this.findVariant(size, input.value);
        mark(input, Boolean(variant && variant.available));
      });

      this.querySelectorAll('[data-option-input="size"]').forEach((input) => {
        const variant = this.findVariant(input.value, color);
        mark(input, Boolean(variant && variant.available));
      });
    }

    /**
     * The belt body, the plate on top of it and the knife over the buckle.
     *
     * The body comes from the colour's own cut-out, never from the variant's
     * catalogue image: those are shot on their own background and framed
     * differently, so using one both dropped a grey rectangle behind the belt
     * and moved the buckle out from under the plate overlay. A colour with no
     * cut-out yet keeps the base image, which is right until the second colour
     * has been photographed.
     */
    updateMedia(variant) {
      const belt = this.beltImage?.closest('.cfg__belt');

      if (this.beltImage) {
        const source = this.colorBases?.[this.selectedColor] || this.defaultBase;
        if (source && this.beltImage.getAttribute('src') !== source) {
          belt?.classList.add('is-swapping');
          this.beltImage.src = source;
          this.beltImage.addEventListener(
            'load',
            () => belt?.classList.remove('is-swapping'),
            { once: true }
          );
        }
      }

      const plate = this.selectedPlate;
      if (this.plateOverlay) {
        const src = plate?.dataset.plateImage;
        if (src) {
          this.plateOverlay.src = src;
          this.plateOverlay.alt = plate.value;
          this.plateOverlay.hidden = false;
        } else {
          this.plateOverlay.hidden = true;
        }
      }

      // Move the row so the chosen plate takes the centre — which is where the
      // belt is, so that slide steps aside and the plate appears on the buckle
      // instead. However the choice was made (an arrow, the step rail, a click
      // straight on a plate), the row lands in the same place.
      const row = this.querySelector('[data-plate-row]');
      if (row && plate) {
        const index = Number(plate.dataset.plateIndex || 0);
        row.style.setProperty('--plate-index', String(index));

        this.querySelectorAll('.cfg__plate').forEach((slide, i) => {
          slide.toggleAttribute('data-centre', i === index);
        });
      }

      // The knife rides in over the buckle when it is added, and back out when
      // it is declined.
      belt?.classList.toggle('is-knife-added', this.knifeSelected);
    }

    /**
     * The bar's second line: the choice made on this step and nothing else —
     * the size card, the leather, the plate. The last step leaves it empty:
     * the summary above already lists the whole belt.
     */
    updateDetail() {
      const line = this.querySelector('[data-bar-selection]');
      if (!line) return;

      const step = this.currentStep;
      let text = '';

      if (step === 'size' || step === 'color') {
        const input = this.querySelector(`[data-option-input="${step}"]:checked`);
        text = input ? this.labelFor(input) : '';
      } else if (step === 'plate') {
        text = this.selectedPlate ? this.labelFor(this.selectedPlate) : '';
      } else if (step === 'knife') {
        text = this.knifeSelected ? this.t('knife_added', 'Knife added') : '';
      } else if (step === 'summary') {
        line.textContent = '';
        return;
      }

      // Nothing chosen here yet — the knife before it is answered — so the
      // line says what the step asks, as the merchant has worded it for the
      // bar, or else the step's heading.
      if (!text) {
        const panel = this.panels.get(step);
        const legend = panel?.querySelector('.cfg__legend');
        text = panel?.dataset.barTitle || (legend ? legend.textContent.trim() : '');
      }

      line.textContent = text;
    }

    /** The visible label of a choice, which is not always its value. */
    labelFor(input) {
      const label = input.closest('label')?.querySelector('.cfg__choice-label, .cfg__plate-caption');
      return label ? label.textContent.trim() : input.value;
    }

    /**
     * The scarcity line. Only ever reports a number Shopify actually holds:
     * untracked inventory and continue-selling variants say "in stock" instead
     * of inventing a count.
     */
    updateStock(variant) {
      const messages = this.querySelectorAll('[data-stock-message]');
      if (!messages.length) return;

      let text = '';
      let level = 'ok';

      if (!variant || !variant.available) {
        text = this.t('out_of_stock', 'Out of stock');
        level = 'out';
      } else if (!variant.inventoryManaged || variant.inventoryPolicy === 'continue') {
        text = this.t('in_stock', 'In stock');
      } else if (this.demoStock) {
        // Proof-of-concept only, behind a theme setting that is off by
        // default: a made-up count, not the store's. See demoCount().
        text = this.t('units_left', '[count] units left').replace('[count]', this.demoCount(variant));
        level = 'low';
      } else {
        const qty = variant.inventoryQuantity;
        if (this.lowStockThreshold > 0 && qty > 0 && qty <= this.lowStockThreshold) {
          // Liquid renders the count as a literal `[count]` placeholder so the
          // surrounding wording stays translatable.
          text =
            qty === 1
              ? this.t('units_left_one', '1 unit left')
              : this.t('units_left', '[count] units left').replace('[count]', qty);
          level = 'low';
        } else {
          text = this.t('in_stock', 'In stock');
        }
      }

      messages.forEach((el) => {
        el.textContent = text;
        el.dataset.level = level;
      });
    }

    /**
     * DEMO ONLY — a random count between the editor's two bounds for each
     * configuration, remembered in this browser for a day so a refresh shows
     * the same number. It is not stock, and the setting that enables it says
     * so: it must be off before the store takes real orders.
     */
    demoCount(variant) {
      const key = `kallo-demo-stock:${variant.id}`;
      const day = 24 * 60 * 60 * 1000;
      try {
        const saved = JSON.parse(localStorage.getItem(key) || 'null');
        if (saved && saved.expires > Date.now()) return saved.count;
      } catch {
        // Storage blocked or unreadable: fall through to a fresh number.
      }

      const { min, max } = this.demoStock;
      const count = min + Math.floor(Math.random() * (max - min + 1));
      try {
        localStorage.setItem(key, JSON.stringify({ count, expires: Date.now() + day }));
      } catch {
        // Not remembered, but still shown.
      }
      return count;
    }

    /**
     * Mirrors each step's current choice into its progress marker, so the rail
     * reads as a row of pictures of the belt being built rather than as four
     * numbers.
     */
    updateProgressThumbs() {
      const plate = this.selectedPlate;
      const plateThumb = plate?.closest('label')?.querySelector('img');
      const knifeImage = this.querySelector('[data-knife-image]');

      const thumbs = {
        size: this.beltImage?.getAttribute('src'),
        color: this.colorBases?.[this.selectedColor] || this.defaultBase,
        plate: plateThumb?.currentSrc || plateThumb?.src || plate?.dataset.plateImage,
        knife: this.knifeSelected ? knifeImage?.getAttribute('src') : null,
      };

      Object.entries(thumbs).forEach(([step, src]) => {
        const el = this.querySelector(`[data-progress-thumb="${step}"]`);
        // A picture set in the theme editor stands for the step whatever is
        // chosen, so it is left alone rather than overwritten on every update.
        if (!el || el.hasAttribute('data-progress-fixed')) return;
        el.style.backgroundImage = src ? `url("${src}")` : '';
      });
    }

    updateSummary(variant) {
      const set = (selector, value) => {
        const el = this.querySelector(selector);
        if (el) el.textContent = value || '—';
      };

      set('[data-summary-size]', this.selectedSize);
      set('[data-summary-color]', this.selectedColor);
      set('[data-summary-plate]', this.selectedPlate?.value);

      const knifeRow = this.querySelector('[data-summary-knife-row]');
      if (knifeRow) {
        knifeRow.hidden = !this.knifeSelected;
        // With a price column, the knife's cost carries the row, so "Included"
        // beside "$49" — which would read as a contradiction — is left off.
        const knifePriced = Boolean(this.querySelector('[data-summary-knife-price]'));
        const knifeValue = this.querySelector('[data-summary-knife]');
        if (knifeValue) {
          knifeValue.textContent =
            this.knifeSelected && !knifePriced ? this.t('included', 'Included') : '';
        }
      }

      // With "Price each line" on, each row carries its own price. The belt's
      // price (FREE when zero) sits on the size row and its compare-at price is
      // struck beside it; the colour adds nothing; the plate and knife carry
      // their upcharges. Every write is guarded, so with the setting off — no
      // price cells in the markup — this does nothing.
      // The belt's price sits on the size row — FREE, with its full retail price
      // struck through. The same figures as the selection bar, so the two agree.
      const beltNow = variant ? this.beltNow(variant) : 0;
      const beltCompare = variant ? this.beltWas(variant) : 0;
      this.setLinePrice('[data-summary-size-price]', '[data-summary-size-compare]', beltNow, beltCompare);
      this.setLinePrice('[data-summary-color-price]', null, 0, 0);

      const plateVariant = this.findPlateVariant();
      const platePriceEl = this.querySelector('[data-summary-plate-price]');
      if (platePriceEl) {
        if (this.plateProduct?.included) {
          platePriceEl.textContent = this.t('included', 'Included');
          this.setLineCompare('[data-summary-plate-compare]', 0, 0);
        } else if (plateVariant && plateVariant.available) {
          this.setLinePrice('[data-summary-plate-price]', '[data-summary-plate-compare]', plateVariant.price, plateVariant.compareAtPrice);
        } else {
          platePriceEl.textContent = '';
          this.setLineCompare('[data-summary-plate-compare]', 0, 0);
        }
      }

      if (this.knifeSelected) {
        this.setLinePrice('[data-summary-knife-price]', '[data-summary-knife-compare]', this.knifePrice, this.knifeCompare);
      }

      // The total the visitor is about to pay, shown with the button that
      // charges it rather than only in the bar below.
      const price = this.querySelector('[data-summary-price]');
      if (price && variant) {
        price.textContent = this.formatMoney(this.runningTotal(variant));
      }
      this.updateOffer(variant);
    }

    /**
     * Fills one summary line's price, and strikes its compare-at price when the
     * part is on offer. Both selectors are optional and missing targets are
     * skipped, so a row without a price column — the default configurator — is
     * left untouched.
     */
    setLinePrice(priceSelector, compareSelector, price, compare) {
      const priceEl = priceSelector && this.querySelector(priceSelector);
      if (priceEl) {
        const amount = price || 0;
        priceEl.textContent = this.formatMoney(amount);
        // A line that works out to nothing reads as the offer word ("FREE").
        // Marking it lets the stylesheet pick it out from the amounts below it
        // — the belt and the colour cost nothing, the plate and knife do not.
        // With no free label set, zero prints as money and the class never
        // lands, so the ordinary configurator is untouched.
        priceEl.classList.toggle(
          'cfg__amount--free',
          Boolean(this.freeLabel) && amount === 0
        );
      }
      if (compareSelector) this.setLineCompare(compareSelector, price, compare);
    }

    setLineCompare(selector, price, compare) {
      const el = this.querySelector(selector);
      if (!el) return;
      const show = compare && compare > (price || 0);
      el.textContent = show ? this.formatMoney(compare) : '';
      el.hidden = !show;
    }

    /* ---- Cart ---- */

    async onSubmit(event) {
      if (!this.currentVariant) return;

      // A plain form post can only carry one line item. Intercept so the belt
      // and the knife are added together, keeping the cart consistent.
      event.preventDefault();

      const errorEl = this.querySelector('[data-cart-error]');
      const submit =
        event.submitter?.closest('[data-add-to-cart]') ||
        this.querySelector('[data-add-to-cart]:not([hidden])') ||
        this.querySelector('[data-add-to-cart]');
      errorEl.hidden = true;

      const items = [
        {
          id: this.currentVariant.id,
          quantity: 1,
          properties: this.collectProperties(),
        },
      ];

      // The plate goes in as its own line, so the cart lists what the visitor
      // actually chose: a belt, a face for it, and possibly a knife. The note
      // stays on the belt line as well, so the belt still says which face it
      // was built with.
      //
      // A face with no variant behind it — not stocked, or the plate product
      // has no matching combination — is left out rather than failing the whole
      // add. Losing the belt over an add-on would be the worse outcome.
      const plateVariant = this.findPlateVariant();
      if (plateVariant && plateVariant.available) {
        items.push({ id: plateVariant.id, quantity: 1 });
      }

      if (this.knifeSelected && this.knifeVariantId) {
        items.push({ id: Number(this.knifeVariantId), quantity: 1 });
      }

      const upsell = this.selectedUpsell;
      if (upsell) items.push({ id: upsell.id, quantity: 1 });

      submit.disabled = true;

      try {
        const response = await fetch(window.Shopify?.routes?.root
          ? `${window.Shopify.routes.root}cart/add.js`
          : '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.description || payload.message || 'Could not add to cart.');
        }

        // The cart is a drawer, so the belt lands in it rather than taking the
        // visitor off the page they just configured. If the drawer is not on
        // the page for any reason, fall back to the cart page.
        const drawer = document.querySelector('kallo-cart-drawer');
        if (drawer && typeof drawer.refresh === 'function') {
          await drawer.refresh();
          drawer.open();
          submit.disabled = false;
        } else {
          window.location.href = window.Shopify?.routes?.root
            ? `${window.Shopify.routes.root}cart`
            : '/cart';
        }
      } catch (error) {
        errorEl.textContent = error.message;
        errorEl.hidden = false;
        submit.disabled = false;
      }
    }

    collectProperties() {
      const properties = {};
      this.querySelectorAll('input[name^="properties["]:checked').forEach((input) => {
        const match = input.name.match(/^properties\[(.+)\]$/);
        if (match) properties[match[1]] = input.value;
      });
      return properties;
    }

    /* ---- Helpers ---- */

    /**
     * Storefront strings are rendered by Liquid, so this only supplies the
     * handful of messages that change after load. Each falls back to English.
     */
    t(key, fallback) {
      return window.KalloStrings?.[key] || fallback;
    }

    /**
     * Mirrors Shopify's money_format placeholders. The shop picks the separator
     * convention, so the total for belt + knife has to match the variant prices
     * rendered by Liquid beside it rather than always emitting 1,234.56.
     */
    formatMoney(cents) {
      // A zero price reads as the offer word ("FREE") when one is set, so the
      // free belt and any line that nets to nothing say so rather than "$0.00".
      if (this.freeLabel && cents === 0) return this.freeLabel;

      const format = window.KalloMoneyFormat || '${{amount}}';

      return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, placeholder) => {
        const decimals = placeholder.includes('no_decimals') ? 0 : 2;
        const [thousands, decimal] = placeholder.includes('comma_separator')
          ? ['.', ',']
          : placeholder.includes('apostrophe_separator')
            ? ["'", '.']
            : [',', '.'];

        const fixed = (cents / 100).toFixed(decimals);
        const [whole, fraction] = fixed.split('.');
        const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

        return fraction ? `${grouped}${decimal}${fraction}` : grouped;
      });
    }
  }

  /**
   * <kallo-360> — drag or arrow-key through a sequence of frames.
   *
   * Frames are only fetched once the viewer opens the view, so a product page
   * never pays for a spin sequence nobody looks at.
   */
  class Kallo360 extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialised) return;
      this.dataset.initialised = 'true';

      try {
        this.frames = JSON.parse(this.dataset.frames || '[]');
      } catch {
        this.frames = [];
      }
      if (!this.frames.length) return;

      this.querySelector('[data-360-toggle]')?.addEventListener('click', () => this.open());
    }

    open() {
      if (this.viewer) {
        this.viewer.hidden = false;
        return;
      }

      this.index = 0;
      this.viewer = document.createElement('div');
      this.viewer.className = 'cfg__360-viewer';
      this.viewer.tabIndex = 0;
      this.viewer.setAttribute('role', 'img');
      this.viewer.setAttribute('aria-label', '360 degree view');

      this.image = document.createElement('img');
      this.image.src = this.frames[0];
      this.image.alt = '';
      this.viewer.appendChild(this.image);
      this.appendChild(this.viewer);

      this.frames.slice(1).forEach((src) => {
        const preload = new Image();
        preload.src = src;
      });

      let dragging = false;
      let lastX = 0;

      const onMove = (x) => {
        const delta = x - lastX;
        if (Math.abs(delta) < 5) return;
        lastX = x;
        this.step(delta > 0 ? 1 : -1);
      };

      this.viewer.addEventListener('pointerdown', (event) => {
        dragging = true;
        lastX = event.clientX;
        this.viewer.setPointerCapture(event.pointerId);
      });
      this.viewer.addEventListener('pointermove', (event) => {
        if (dragging) onMove(event.clientX);
      });
      this.viewer.addEventListener('pointerup', () => (dragging = false));
      this.viewer.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.step(1);
        if (event.key === 'ArrowLeft') this.step(-1);
      });
    }

    step(direction) {
      this.index = (this.index + direction + this.frames.length) % this.frames.length;
      this.image.src = this.frames[this.index];
    }
  }

  if (!customElements.get('kallo-configurator')) {
    customElements.define('kallo-configurator', KalloConfigurator);
  }
  if (!customElements.get('kallo-360')) {
    customElements.define('kallo-360', Kallo360);
  }
})();
